import * as React from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { parseISO, addDays, startOfDay, isAfter } from "date-fns";
import { cloneDeep, isEqual } from "lodash";
import {
    Alert,
    DatePicker,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    FormCheckbox,
    Input,
    RadioInput,
    Select,
    TextArea,
} from "@jhool-io/fe-components";
import {
    IAiSessionNoteDraft,
    IAiSessionNoteWriterContext,
    IEditInitialSoapNote,
    IGetClientNotes,
    ISessionNote,
    NoteTypes,
} from "../../../../modules/notes/types/notes.types";

import { AppointmentLocation } from "../../../../utils/types/session";
import {
    formatDate,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../utils/helpers";
import NoteFormInfoBox from "../../NoteFormInfoBox/NoteFormInfoBox";
import { useFetchClientNotesList } from "../../../../hooks/queries/note";
import LoadPreviousSessionNotificationModal from "../../LoadPreviousSessionNotificatiModal/LoadPreviousSessionNotificationModal";
import { INITIAL_SOAP_NOTE_LABELS } from "../../../../utils/constants";
import EditNoteFooter from "../../../../modules/notes/components/NoteCreationFooters/EditNoteFooter/EditNoteFooter";
import {
    useEditNote,
    useNoteAutosave,
    useNoteValidationFeedback,
} from "../../../../modules/notes/hooks/edit-note";
import DiagnosisCodeSelect from "../../../../modules/notes/components/DiagnosisCodeSelect/DiagnosisCodeSelect";
import AiSessionNoteActions from "../../../../modules/notes/components/AiSessionNoteActions/AiSessionNoteActions";
import AiFieldCue from "../../../../modules/notes/components/AiWriterFieldCue/AiWriterFieldCue";
import {
    AiSessionNoteReviewFieldAnnotation,
    AiSessionNoteReviewPanel,
} from "../../../../modules/notes/components/AiSessionNoteReview/AiSessionNoteReview";
import {
    AiSessionNoteReviewBoundary,
    useAiSessionNoteReview,
} from "../../../../context/AiSessionNoteReviewContext";
import {
    AI_GENERATED_NOTE_FIELDS,
    AiReviewFormField,
    AiWriterCueField,
    buildAiWriterContext,
    buildAiWriterDraftSnapshot,
    buildAiWriterFormValues,
    formatAiWriterDateOfService,
    getAiWriterDraftUpdates,
    getPopulatedAiWriterFields,
    mergeAiWriterInterventionSelections,
    resolveAiWriterContext,
} from "../../../../modules/notes/helpers/aiSessionNotes";
import { createAiSessionNoteReviewSaveCoordinator } from "../../../../modules/notes/helpers/aiSessionNoteReviewSave";
import { useAiSessionNotePreSubmitReview } from "../../../../modules/notes/hooks/useAiSessionNotePreSubmitReview";
import { useAiWriterContinuityNotes } from "../../../../modules/notes/hooks/useAiWriterContinuityNotes";
import { useAiWriterIntakeNotes } from "../../../../modules/notes/hooks/useAiWriterIntakeNotes";

interface EditInitialSoapNoteProps {
    noteDetails: ISessionNote;
}

type Option = {
    label: React.ReactNode;
    value: string;
};

function EditInitialSoapNoteForm({ noteDetails }: EditInitialSoapNoteProps) {
    // Local component state
    const [localDateOfNextSession, setLocalDateOfNextSession] =
        React.useState<Date | null>(
            noteDetails?.note_content?.date_of_next_session
                ? parseISO(noteDetails?.note_content?.date_of_next_session)
                : null
        );
    const [showPrompt, setShowPrompt] = React.useState(false);
    const [selectedNote, setSelectedNote] = React.useState(
        {} as IGetClientNotes
    );

    const isSigning = React.useRef(false);

    const { handleAutoSaveNote, handleEditDraftNote, handleSignNote } =
        useEditNote();

    const clientNoteParams = {
        type: noteDetails?.type as string,
        load_previous_notes: true,
        provider_id: noteDetails?.provider?.provider_id as string,
        before_date: formatAiWriterDateOfService(noteDetails.date_of_service),
    };

    const { data: notes } = useFetchClientNotesList(
        noteDetails?.client?.client_id as string,
        clientNoteParams
    );
    const { data: aiContinuityNotes } = useAiWriterContinuityNotes(noteDetails);

    const {
        register,
        handleSubmit,
        control,
        watch,
        setValue,
        getValues,
        trigger,
        formState: { errors, isDirty, submitCount },
    } = useForm<IEditInitialSoapNote>({
        resolver: yupResolver(
            yup
                .object()
                .shape({
                    duration: yup.string(),
                    appointment_location: yup.string().when([], {
                        is: () => isSigning.current,
                        then: yup
                            .string()
                            .required("Session location is required"),
                        otherwise: yup.string(),
                    }),
                    delivery_option: yup
                        .string()
                        .nullable()
                        .when(
                            "appointment_location",
                            (appointment_location, field) =>
                                appointment_location === "teletherapy" &&
                                isSigning.current
                                    ? field.required(
                                          "Client teletherapy location is required"
                                      )
                                    : field
                        ),
                    provider_location: yup
                        .string()
                        .nullable()
                        .when(
                            "appointment_location",
                            (appointment_location, field) =>
                                appointment_location === "teletherapy" &&
                                isSigning.current
                                    ? field.required(
                                          "Provider teletherapy location is required"
                                      )
                                    : field
                        ),
                    consent_was_received_for_the_teletherapy_session:
                        yup.bool(),
                    the_teletherapy_session_was_conducted_via_video: yup.bool(),
                    other_delivery_option: yup
                        .string()
                        .when("delivery_option", (delivery_option, field) =>
                            delivery_option === "Other" && isSigning.current
                                ? field
                                      .trim()
                                      .required(
                                          "Other client teletherapy location is required"
                                      )
                                : field
                        ),
                    other_provider_location: yup
                        .string()
                        .when("provider_location", (provider_location, field) =>
                            provider_location === "Other" && isSigning.current
                                ? field
                                      .trim()
                                      .required(
                                          "Other provider teletherapy location is required"
                                      )
                                : field
                        ),
                    diagnosis_codes: yup.array().when([], {
                        is: () => isSigning.current,
                        then: yup
                            .array()
                            .max(1, "You can only select 1 diagnosis")
                            .min(1, "Diagnosis is required")
                            .required("Diagnosis is required"),
                        otherwise: yup
                            .array()
                            .nullable()
                            .max(1, "You can only select 1 diagnosis"),
                    }),
                    secondary_diagnosis: yup
                        .array()
                        .nullable()
                        .max(1, "You can only select 1 diagnosis"),
                    tertiary_diagnosis: yup
                        .array()
                        .nullable()
                        .max(1, "You can only select 1 diagnosis"),
                    additional_diagnosis_one: yup
                        .array()
                        .nullable()
                        .max(1, "You can only select 1 diagnosis"),
                    additional_diagnosis_two: yup
                        .array()
                        .nullable()
                        .max(1, "You can only select 1 diagnosis"),
                    isClientPresent: yup.bool(),
                    emotional_behavioural_symptoms_demonstrated: yup
                        .string()
                        .when([], {
                            is: () => isSigning.current,
                            then: yup
                                .string()
                                .trim()
                                .required(
                                    "Emotional behavioural symptoms demonstrated is required"
                                )
                                .min(
                                    20,
                                    "Emotional behavioural symptoms demonstrated must contain at least 20 characters"
                                ),
                            otherwise: yup.string(),
                        }),
                    causing: yup.string().when([], {
                        is: () => isSigning.current,
                        then: yup.string().required("Causing is required"),
                        otherwise: yup.string(),
                    }),
                    intention_for_session: yup.string().when([], {
                        is: () => isSigning.current,
                        then: yup
                            .string()
                            .required("Intention for session is required"),
                        otherwise: yup.string(),
                    }),
                    subjective: yup.string().when([], {
                        is: () => isSigning.current,
                        then: yup
                            .string()
                            .trim()
                            .required("Subjective field is required")
                            .min(
                                20,
                                "Subjective field must contain at least 20 characters"
                            ),
                        otherwise: yup.string(),
                    }),
                    objective: yup.string().when([], {
                        is: () => isSigning.current,
                        then: yup
                            .string()
                            .trim()
                            .required("Objective field is required")
                            .min(
                                20,
                                "Objective field must contain at least 20 characters"
                            ),
                        otherwise: yup.string(),
                    }),
                    assessment: yup.string().when([], {
                        is: () => isSigning.current,
                        then: yup
                            .string()
                            .trim()
                            .required("Assessment field is required")
                            .min(
                                20,
                                "Assessment field must contain at least 20 characters"
                            ),
                        otherwise: yup.string(),
                    }),
                    cognitive_behavioral_therapies: yup.array().nullable(),
                    relationship_based_interventions: yup.array().nullable(),
                    other: yup.array().nullable(),
                    plan: yup.string().when([], {
                        is: () => isSigning.current,
                        then: yup
                            .string()
                            .trim()
                            .required("Plan field is required")
                            .min(
                                20,
                                "Plan field must contain at least 20 characters"
                            ),
                        otherwise: yup.string(),
                    }),
                    frequency_of_sessions: yup.string().when([], {
                        is: () => isSigning.current,
                        then: yup.string().required("This field is required"),
                        otherwise: yup.string().nullable(),
                    }),
                    other_frequency_of_sessions: yup
                        .string()
                        .when(
                            "frequency_of_sessions",
                            (frequency_of_sessions, field) =>
                                frequency_of_sessions === "Other" &&
                                isSigning.current
                                    ? field
                                          .trim()
                                          .required(
                                              "Other frequency of sessions is required"
                                          )
                                    : field
                        ),
                    date_of_next_session: yup.date().when([], {
                        is: () => isSigning.current,
                        then: yup
                            .date()
                            .required("Date of next session is required")
                            .test(
                                "is-after-dos",
                                "Date of next session must be after the current session date",
                                function validateDateAfterDOS(value) {
                                    if (!value) return false;
                                    if (!noteDetails.date_of_service)
                                        return true;
                                    const dos = startOfDay(
                                        parseISO(noteDetails.date_of_service)
                                    );
                                    const nextSession = startOfDay(value);
                                    return isAfter(nextSession, dos);
                                }
                            ),
                        otherwise: yup.date().nullable(),
                    }),
                    prognosis: yup.string().when([], {
                        is: () => isSigning.current,
                        then: yup
                            .string()
                            .trim()
                            .required("Prognosis is required")
                            .min(
                                20,
                                "Prognosis must contain at least 20 characters"
                            ),
                        otherwise: yup.string(),
                    }),
                    client_response: yup.string().when([], {
                        is: () => isSigning.current,
                        then: yup
                            .string()
                            .trim()
                            .required("Client response is required")
                            .min(
                                20,
                                "Client response must contain at least 20 characters"
                            ),
                        otherwise: yup.string(),
                    }),
                    goal_one: yup.string().when([], {
                        is: () => isSigning.current,
                        then: yup
                            .string()
                            .trim()
                            .required("Goal is required")
                            .min(
                                20,
                                "Goal must contain at least 20 characters"
                            ),
                        otherwise: yup.string(),
                    }),
                    progress_one: yup.string().when([], {
                        is: () => isSigning.current,
                        then: yup
                            .string()
                            .trim()
                            .required("Progress is required")
                            .min(
                                20,
                                "Progress must contain at least 20 characters"
                            ),
                        otherwise: yup.string(),
                    }),
                    goal_two: yup.string().when([], {
                        is: () => isSigning.current,
                        then: yup
                            .string()
                            .trim()
                            .required("Goal two is required")
                            .min(
                                20,
                                "Goal two must contain at least 20 characters"
                            ),
                        otherwise: yup.string(),
                    }),
                    progress_two: yup.string().when([], {
                        is: () => isSigning.current,
                        then: yup
                            .string()
                            .trim()
                            .required("Progress two is required")
                            .min(
                                20,
                                "Progress two must contain at least 20 characters"
                            ),
                        otherwise: yup.string(),
                    }),
                    goal_three: yup.string(),
                    progress_three: yup.string(),
                    risk_and_safety_language: yup
                        .string()
                        .nullable()
                        .when([], {
                            is: () => isSigning.current,
                            then: yup
                                .string()
                                .nullable()
                                .required(
                                    "Risk and safety language is required"
                                ),
                            otherwise: yup.string().nullable(),
                        }),
                    new_risk_concerns_details: yup
                        .string()
                        .nullable()
                        .when(
                            "risk_and_safety_language",
                            (risk_and_safety_language, field) =>
                                isSigning.current &&
                                risk_and_safety_language ===
                                    "New risk concerns reviewed with supervisor"
                                    ? field
                                          .trim()
                                          .required(
                                              "New risk concerns details is required"
                                          )
                                          .min(
                                              20,
                                              "New risk concerns details must contain at least 20 characters"
                                          )
                                    : field
                        ),
                    additional_client_reported_information_post_intake:
                        yup.string(),
                    signature: yup.string().when([], {
                        is: () => isSigning.current,
                        then: yup
                            .string()
                            .trim()
                            .required("Signature is required"),
                        otherwise: yup.string(),
                    }),
                })
                .test(
                    "at-least-one-item-in-array",
                    "Please select at least one option from the Therapy Intervention Techniques fields",
                    (values) => {
                        if (!isSigning.current) return true;

                        const arrays = [
                            values.cognitive_behavioral_therapies,
                            values.relationship_based_interventions,
                            values.other,
                        ];

                        return arrays.some(
                            (arr) => Array.isArray(arr) && arr.length > 0
                        );
                    }
                )
        ),
        mode: "onChange",
        defaultValues: noteDetails.note_content
            ? {
                  ...noteDetails.note_content,
                  secondary_diagnosis: noteDetails?.note_content
                      ?.secondary_diagnosis
                      ? [noteDetails?.note_content?.secondary_diagnosis]
                      : undefined,
                  tertiary_diagnosis: noteDetails?.note_content
                      ?.tertiary_diagnosis
                      ? [noteDetails?.note_content?.tertiary_diagnosis]
                      : undefined,
                  additional_diagnosis_one: noteDetails?.note_content
                      ?.additional_diagnosis_one
                      ? [noteDetails?.note_content?.additional_diagnosis_one]
                      : undefined,
                  additional_diagnosis_two: noteDetails?.note_content
                      ?.additional_diagnosis_two
                      ? [noteDetails?.note_content?.additional_diagnosis_two]
                      : undefined,
              }
            : undefined,
        shouldFocusError: false,
    });

    const watchedValues = watch();
    const { data: intakeNotes } = useAiWriterIntakeNotes(
        noteDetails,
        watchedValues
    );
    const { review: aiReview, markReviewStale } = useAiSessionNoteReview();
    const [aiWriterDraftSnapshot, setAiWriterDraftSnapshot] =
        React.useState<ReturnType<typeof buildAiWriterDraftSnapshot> | null>(
            null
        );
    const hasUnsavedProgrammaticChanges = React.useRef(false);
    const reviewedValuesRef = React.useRef<IEditInitialSoapNote | null>(null);
    const reviewSaveCoordinatorRef = React.useRef(
        createAiSessionNoteReviewSaveCoordinator<IEditInitialSoapNote>(
            watchedValues
        )
    );
    useNoteValidationFeedback({
        errors,
        submitCount,
        isSigningRef: isSigning,
        trigger,
    });

    const { cancelScheduledSave, skipCurrentValues } = useNoteAutosave({
        watchedValues,
        getValues,
        handleSubmit,
        onSave: async (values) => {
            const savedValues = cloneDeep(values);
            const didSave = await reviewSaveCoordinatorRef.current.save(
                savedValues,
                handleAutoSaveNote
            );
            if (didSave && isEqual(savedValues, getValues())) {
                hasUnsavedProgrammaticChanges.current = false;
            }
            return didSave;
        },
        isSigningRef: isSigning,
        onChange: () => {
            if (aiReview && reviewedValuesRef.current) {
                markReviewStale();
            }
        },
    });

    const handleApplyAiDraft = (
        draft: IAiSessionNoteDraft,
        context: IAiSessionNoteWriterContext
    ) => {
        setAiWriterDraftSnapshot(buildAiWriterDraftSnapshot(draft));
        const draftUpdates = getAiWriterDraftUpdates(draft);
        AI_GENERATED_NOTE_FIELDS.forEach((field) => {
            const value = draftUpdates[field];
            if (typeof value === "string") {
                setValue(field, value, { shouldDirty: true });
            }
        });
        setValue(
            "cognitive_behavioral_therapies",
            mergeAiWriterInterventionSelections(
                getValues("cognitive_behavioral_therapies"),
                draft.note_content.cognitive_behavioral_therapies
            ),
            { shouldDirty: true }
        );
        setValue(
            "relationship_based_interventions",
            mergeAiWriterInterventionSelections(
                getValues("relationship_based_interventions"),
                draft.note_content.relationship_based_interventions
            ),
            { shouldDirty: true }
        );
        setValue(
            "other",
            mergeAiWriterInterventionSelections(
                getValues("other"),
                draft.note_content.other
            ),
            { shouldDirty: true }
        );
        const formContext = buildAiWriterFormValues(context);
        setValue("goal_one", formContext.goal_one, { shouldDirty: true });
        setValue("goal_two", formContext.goal_two, { shouldDirty: true });
        setValue("goal_three", formContext.goal_three, { shouldDirty: true });
        hasUnsavedProgrammaticChanges.current = true;
        markReviewStale();
        skipCurrentValues();
    };

    const renderAiWriterFieldCue = (field: AiWriterCueField) => (
        <AiFieldCue
            field={field}
            snapshot={aiWriterDraftSnapshot}
            value={watchedValues[field]}
        />
    );

    const renderAiReviewFieldAnnotation = (field: AiReviewFormField) => (
        <AiSessionNoteReviewFieldAnnotation field={field} />
    );

    const renderAiReviewFieldHeading = (
        field: AiReviewFormField,
        label: string,
        className = "note-label-req"
    ) => (
        <div>
            <label htmlFor={field} className={`block ${className}`}>
                {label}
            </label>
            {renderAiReviewFieldAnnotation(field)}
        </div>
    );

    const flushNoteForAiReview = React.useCallback(async () => {
        const reviewFormValues = cloneDeep(getValues());
        cancelScheduledSave();
        let didSave = false;
        const saveForReview = async (values: IEditInitialSoapNote) => {
            const savedValues = cloneDeep(values);
            if (!isDirty && !hasUnsavedProgrammaticChanges.current) {
                reviewSaveCoordinatorRef.current.synchronizeSavedValues(
                    savedValues
                );
            }
            didSave = await reviewSaveCoordinatorRef.current.prepareReview(
                savedValues,
                handleAutoSaveNote
            );
            if (!didSave) return;
            reviewedValuesRef.current = reviewFormValues;
            const currentValues = getValues();
            if (isEqual(reviewFormValues, currentValues)) {
                skipCurrentValues();
                hasUnsavedProgrammaticChanges.current = false;
            }
        };
        isSigning.current = false;
        await handleSubmit(saveForReview)();
        return didSave;
    }, [
        cancelScheduledSave,
        getValues,
        handleAutoSaveNote,
        handleSubmit,
        isDirty,
        skipCurrentValues,
    ]);

    const {
        handleSubmit: handleSubmitAfterAiReview,
        isSubmitDisabled: isAiSubmitReviewPending,
        submitButtonLabel,
    } = useAiSessionNotePreSubmitReview<IEditInitialSoapNote>({
        beforeReview: flushNoteForAiReview,
        getValues,
        onSubmit: handleSignNote,
        reviewedValuesRef,
    });

    React.useEffect(() => {
        if (
            aiReview &&
            reviewedValuesRef.current &&
            !isEqual(reviewedValuesRef.current, getValues())
        ) {
            markReviewStale();
        }
    }, [aiReview, getValues, markReviewStale]);

    React.useEffect(() => {
        setAiWriterDraftSnapshot(null);
        reviewedValuesRef.current = null;
        hasUnsavedProgrammaticChanges.current = false;
        reviewSaveCoordinatorRef.current.reset(getValues());
    }, [getValues, noteDetails.note_id]);

    // Get causing options for select
    const CausingOptionsForSelect = [
        "Maladaptive Functioning",
        "Inappropriate Behaviour",
        "Maladaptive Functioning & Inappropriate Behaviour",
    ].map((cause) => ({
        label: cause,
        value: cause,
    }));

    // Get intention for session options for select
    const IntentionForSessionForSelect = [
        "Attempts to alleviate the emotional disturbances",
        "Reverse or change maladaptive patterns of behavior",
        "Facilitate coping mechanisms",
        "Encourage personality growth and development",
        "Encourage personality growth and minimize maladaptive functioning",
        "Facilitate coping mechanisms",
    ].map((intention) => ({
        label: intention,
        value: intention,
    }));

    // Get appointment location options
    const AppointmentLocationsForSelect = Object.values(
        AppointmentLocation
    ).map((location) => {
        return {
            label: removeEnumUnderscore(makeStringFirstLetterCapital(location)),
            value: location,
        };
    });

    const handleCheckCognitive = (checkboxValue: string) => {
        return Boolean(
            noteDetails.note_content?.cognitive_behavioral_therapies?.find(
                (value: string) => value.trim() === checkboxValue.trim()
            )
        );
    };

    const handleCheckRelationship = (checkboxValue: string) => {
        return Boolean(
            noteDetails.note_content?.relationship_based_interventions?.find(
                (value: string) => value.trim() === checkboxValue.trim()
            )
        );
    };

    const handleCheckOther = (checkboxValue: string) => {
        return Boolean(
            noteDetails.note_content?.other?.find(
                (value: string) => value.trim() === checkboxValue.trim()
            )
        );
    };

    // Function to handle item click and set the selected note content
    const handleLoadPreviousSession = (note: IGetClientNotes) => {
        hasUnsavedProgrammaticChanges.current = true;
        setValue(
            "appointment_location",
            note.note_content.appointment_location
        );
        setValue("assessment", note.note_content.assessment);
        setValue("causing", note.note_content.causing);
        setValue("client_response", note.note_content.client_response);
        setValue(
            "cognitive_behavioral_therapies",
            note.note_content.cognitive_behavioral_therapies
        );
        setValue(
            "consent_was_received_for_the_teletherapy_session",
            note.note_content.consent_was_received_for_the_teletherapy_session
        );
        setValue("delivery_option", note.note_content.delivery_option);
        setValue("diagnosis_codes", note.note_content.diagnosis_codes);
        setValue("secondary_diagnosis", [
            note.note_content.secondary_diagnosis,
        ]);
        setValue("tertiary_diagnosis", [note.note_content.tertiary_diagnosis]);
        setValue("additional_diagnosis_one", [
            note.note_content.additional_diagnosis_one,
        ]);
        setValue("additional_diagnosis_two", [
            note.note_content.additional_diagnosis_two,
        ]);
        setValue("duration", note.note_content.duration);
        setValue(
            "emotional_behavioural_symptoms_demonstrated",
            note.note_content.emotional_behavioural_symptoms_demonstrated
        );
        setValue("goal_one", note.note_content.goal_one);
        setValue("goal_two", note.note_content.goal_two);
        setValue("goal_three", note.note_content.goal_three);
        setValue(
            "intention_for_session",
            note.note_content.intention_for_session
        );
        setValue("isClientPresent", note.note_content.isClientPresent);
        setValue("objective", note.note_content.objective);
        setValue("other", note.note_content.other);
        setValue(
            "other_delivery_option",
            note.note_content.other_delivery_option
        );
        setValue(
            "other_provider_location",
            note.note_content.other_provider_location
        );
        setValue("plan", note.note_content.plan);
        setValue("prognosis", note.note_content.prognosis);
        setValue("progress_one", note.note_content.progress_one);
        setValue("progress_three", note.note_content.progress_three);
        setValue("progress_two", note.note_content.progress_two);
        setValue("provider_location", note.note_content.provider_location);
        setValue(
            "relationship_based_interventions",
            note.note_content.relationship_based_interventions
        );
        setValue("subjective", note.note_content.subjective);
        setValue(
            "the_teletherapy_session_was_conducted_via_video",
            note.note_content.the_teletherapy_session_was_conducted_via_video
        );
        setValue(
            "frequency_of_sessions",
            note.note_content.frequency_of_sessions
        );
        setValue(
            "other_frequency_of_sessions",
            note.note_content.other_frequency_of_sessions
        );
        setValue(
            "risk_and_safety_language",
            note.note_content.risk_and_safety_language
        );
        setValue(
            "new_risk_concerns_details",
            note.note_content.new_risk_concerns_details
        );
        setValue(
            "additional_client_reported_information_post_intake",
            note.note_content.additional_client_reported_information_post_intake
        );
        setShowPrompt(false);
    };

    const rootError = errors
        ? Object.values(errors).find(
              (val) => val.type === "at-least-one-item-in-array"
          )?.message
        : undefined;

    return (
        <div>
            <LoadPreviousSessionNotificationModal
                showPrompt={showPrompt}
                setShowPrompt={setShowPrompt}
                handleLoadPreviousSession={() =>
                    handleLoadPreviousSession(selectedNote)
                }
            />
            <AiSessionNoteReviewPanel beforeReview={flushNoteForAiReview} />
            <div className="flex flex-wrap justify-end gap-3 mb-[--spacing-header]">
                <AiSessionNoteActions
                    noteId={noteDetails.note_id}
                    getNoteContext={() =>
                        resolveAiWriterContext(
                            buildAiWriterContext(
                                getValues() as unknown as Record<
                                    string,
                                    unknown
                                >
                            ),
                            [
                                ...(aiContinuityNotes?.data ?? []),
                                ...(notes?.data ?? []),
                                ...(intakeNotes?.data ?? []),
                            ],
                            noteDetails
                        )
                    }
                    getExistingContentFields={() =>
                        getPopulatedAiWriterFields(
                            getValues() as unknown as Record<string, unknown>
                        )
                    }
                    onApplyDraft={handleApplyAiDraft}
                    beforeReview={flushNoteForAiReview}
                />
                {notes && notes.data.length > 0 ? (
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <FilterButton>
                                Load previous session note
                            </FilterButton>
                        </DropdownTrigger>
                        <DropdownContent width="auto" align="end">
                            {notes &&
                                notes?.data?.map((note) => (
                                    <DropdownItem
                                        key={note.note_id}
                                        onClick={() => {
                                            setShowPrompt(true);
                                            setSelectedNote(note);
                                        }}
                                    >
                                        <div>
                                            {makeStringFirstLetterCapital(
                                                removeEnumUnderscore(
                                                    note.note_type
                                                )
                                            )}{" "}
                                            - Note content Details -{" "}
                                            {formatDate(note.date_of_service)}
                                        </div>
                                    </DropdownItem>
                                ))}
                        </DropdownContent>
                    </Dropdown>
                ) : null}
            </div>
            <form
                onSubmit={(event) => {
                    isSigning.current = true;
                    handleSubmit(handleSubmitAfterAiReview)(event);
                }}
                aria-label="soap form"
                id="edit-note-form"
            >
                <div className="mb-[--spacing-header]">
                    <NoteFormInfoBox />
                </div>
                <section id="step-one" className="mb-10">
                    <div className="mb-2">
                        <Alert
                            description="Initial diagnostic evaluation, diagnosis, and medical necessity were established by a licensed clinician."
                            type="info"
                        />
                    </div>
                    {(noteDetails.type === NoteTypes.FAMILY_SOAP_NOTE ||
                        noteDetails.type ===
                            NoteTypes.FAMILY_INITIAL_SOAP_NOTE) && (
                        <div className="fg">
                            <FormCheckbox
                                {...register("isClientPresent")}
                                label={INITIAL_SOAP_NOTE_LABELS.isClientPresent}
                                defaultChecked={
                                    noteDetails.note_content?.isClientPresent
                                }
                            />
                        </div>
                    )}
                    <div className="fg">
                        <Controller
                            name="appointment_location"
                            control={control}
                            render={({ field }) => (
                                <Select
                                    isRequired
                                    label={
                                        INITIAL_SOAP_NOTE_LABELS.appointment_location
                                    }
                                    placeholder=""
                                    onChange={(val) =>
                                        field.onChange((val as Option).value)
                                    }
                                    value={
                                        AppointmentLocationsForSelect.find(
                                            (location) =>
                                                location.value === field.value
                                        ) || null
                                    }
                                    options={AppointmentLocationsForSelect}
                                    hasError={!!errors.appointment_location}
                                    errorText={
                                        errors.appointment_location?.message
                                    }
                                />
                            )}
                        />
                    </div>
                    {watch(`appointment_location`) ===
                        AppointmentLocation.TELETHERAPY && (
                        <>
                            <p className="note-label note-label-opt">
                                {INITIAL_SOAP_NOTE_LABELS.delivery_option}
                            </p>
                            <div className="fg fg-space-between three flex">
                                <RadioInput
                                    {...register(`delivery_option`)}
                                    label="Home"
                                    value="Home"
                                    defaultChecked={
                                        noteDetails.note_content
                                            ?.delivery_option === "Home"
                                    }
                                />

                                <RadioInput
                                    {...register(`delivery_option`)}
                                    label="Office"
                                    value="Office"
                                    defaultChecked={
                                        noteDetails.note_content
                                            ?.delivery_option === "Office"
                                    }
                                />

                                <RadioInput
                                    {...register(`delivery_option`)}
                                    label="Other"
                                    value="Other"
                                    defaultChecked={
                                        noteDetails.note_content
                                            ?.delivery_option === "Other"
                                    }
                                />
                            </div>
                            {errors.delivery_option ? (
                                <div className="note-error">
                                    <p>Delivery option is required</p>
                                </div>
                            ) : null}
                            {watch(`delivery_option`) === "Other" ||
                            noteDetails.note_content?.delivery_option ===
                                "Other" ? (
                                <div className="fg">
                                    <Input
                                        {...register(`other_delivery_option`)}
                                        label={
                                            INITIAL_SOAP_NOTE_LABELS.other_delivery_option
                                        }
                                        hasError={
                                            !!errors?.other_delivery_option
                                        }
                                        errorText={
                                            errors?.other_delivery_option
                                                ?.message
                                        }
                                        defaultValue={
                                            noteDetails.note_content
                                                ?.other_delivery_option
                                        }
                                    />
                                </div>
                            ) : null}
                        </>
                    )}
                    {watch(`appointment_location`) ===
                        AppointmentLocation.TELETHERAPY && (
                        <>
                            <p className="note-label">
                                {INITIAL_SOAP_NOTE_LABELS.provider_location}
                            </p>
                            <div className="fg fg-space-between three flex">
                                <RadioInput
                                    {...register(`provider_location`)}
                                    label="Home"
                                    value="Home"
                                    defaultChecked={
                                        noteDetails.note_content
                                            ?.provider_location === "Home"
                                    }
                                />

                                <RadioInput
                                    {...register(`provider_location`)}
                                    label="Office"
                                    value="Office"
                                    defaultChecked={
                                        noteDetails.note_content
                                            ?.provider_location === "Office"
                                    }
                                />

                                <RadioInput
                                    {...register(`provider_location`)}
                                    label="Other"
                                    value="Other"
                                    defaultChecked={
                                        noteDetails.note_content
                                            ?.provider_location === "Other"
                                    }
                                />
                            </div>
                            {errors.provider_location ? (
                                <div className="note-error">
                                    <p>Delivery option is required</p>
                                </div>
                            ) : null}
                            {watch(`provider_location`) === "Other" ||
                            noteDetails.note_content?.provider_location ===
                                "Other" ? (
                                <div className="fg">
                                    <Input
                                        {...register(`other_provider_location`)}
                                        label={
                                            INITIAL_SOAP_NOTE_LABELS.other_provider_location
                                        }
                                        hasError={
                                            !!errors?.other_provider_location
                                        }
                                        errorText={
                                            errors?.other_provider_location
                                                ?.message
                                        }
                                        defaultValue={
                                            noteDetails.note_content
                                                ?.other_provider_location
                                        }
                                    />
                                </div>
                            ) : null}
                        </>
                    )}
                    {watch("appointment_location") ===
                    AppointmentLocation.TELETHERAPY ? (
                        <div className="note-checkboxes">
                            <FormCheckbox
                                {...register(
                                    "consent_was_received_for_the_teletherapy_session"
                                )}
                                label={
                                    INITIAL_SOAP_NOTE_LABELS.consent_was_received_for_the_teletherapy_session
                                }
                                defaultChecked={
                                    noteDetails.note_content
                                        ?.consent_was_received_for_the_teletherapy_session
                                }
                            />
                            <FormCheckbox
                                {...register(
                                    "the_teletherapy_session_was_conducted_via_video"
                                )}
                                label={
                                    INITIAL_SOAP_NOTE_LABELS.the_teletherapy_session_was_conducted_via_video
                                }
                                defaultChecked={
                                    noteDetails.note_content
                                        ?.the_teletherapy_session_was_conducted_via_video
                                }
                            />
                        </div>
                    ) : null}
                    <div className="fg">
                        <Controller
                            name="diagnosis_codes"
                            control={control}
                            render={({ field }) => (
                                <DiagnosisCodeSelect
                                    isRequired
                                    label={
                                        INITIAL_SOAP_NOTE_LABELS.diagnosis_codes
                                    }
                                    value={field.value}
                                    onChange={field.onChange}
                                    hideSelectedOptions
                                    errorText={
                                        errors.diagnosis_codes?.type ===
                                        "typeError"
                                            ? "Diagnosis codes are required"
                                            : errors.diagnosis_codes?.message
                                    }
                                    isMulti
                                />
                            )}
                        />
                    </div>
                    <div className="fg">
                        <Controller
                            name="secondary_diagnosis"
                            control={control}
                            render={({ field }) => (
                                <DiagnosisCodeSelect
                                    label={
                                        INITIAL_SOAP_NOTE_LABELS.secondary_diagnosis
                                    }
                                    value={field.value}
                                    onChange={field.onChange}
                                    errorText={
                                        errors.secondary_diagnosis?.message
                                    }
                                    isOptional
                                    isMulti
                                />
                            )}
                        />
                    </div>
                    <div className="fg">
                        <Controller
                            name="tertiary_diagnosis"
                            control={control}
                            render={({ field }) => (
                                <DiagnosisCodeSelect
                                    label={
                                        INITIAL_SOAP_NOTE_LABELS.tertiary_diagnosis
                                    }
                                    value={field.value}
                                    onChange={field.onChange}
                                    errorText={
                                        errors.tertiary_diagnosis?.message
                                    }
                                    isOptional
                                    isMulti
                                />
                            )}
                        />
                    </div>
                    <div className="fg">
                        <Controller
                            name="additional_diagnosis_one"
                            control={control}
                            render={({ field }) => (
                                <DiagnosisCodeSelect
                                    label={
                                        INITIAL_SOAP_NOTE_LABELS.additional_diagnosis_one
                                    }
                                    value={field.value}
                                    onChange={field.onChange}
                                    errorText={
                                        errors.additional_diagnosis_one?.message
                                    }
                                    isOptional
                                    isMulti
                                />
                            )}
                        />
                    </div>
                    <div className="fg">
                        <Controller
                            name="additional_diagnosis_two"
                            control={control}
                            render={({ field }) => (
                                <DiagnosisCodeSelect
                                    label={
                                        INITIAL_SOAP_NOTE_LABELS.additional_diagnosis_two
                                    }
                                    value={field.value}
                                    onChange={field.onChange}
                                    errorText={
                                        errors.additional_diagnosis_two?.message
                                    }
                                    isOptional
                                    isMulti
                                />
                            )}
                        />
                    </div>
                    <div className="fg">
                        {renderAiReviewFieldHeading(
                            "emotional_behavioural_symptoms_demonstrated",
                            INITIAL_SOAP_NOTE_LABELS.emotional_behavioural_symptoms_demonstrated
                        )}
                        <TextArea
                            {...register(
                                "emotional_behavioural_symptoms_demonstrated"
                            )}
                            id="emotional_behavioural_symptoms_demonstrated"
                            hasError={
                                !!errors.emotional_behavioural_symptoms_demonstrated
                            }
                            defaultValue={
                                noteDetails.note_content
                                    ?.emotional_behavioural_symptoms_demonstrated
                            }
                            errorText={
                                errors
                                    .emotional_behavioural_symptoms_demonstrated
                                    ?.message
                            }
                            isRequired
                        />
                        {renderAiWriterFieldCue(
                            "emotional_behavioural_symptoms_demonstrated"
                        )}
                    </div>
                    <div className="fg">
                        {renderAiReviewFieldHeading(
                            "causing",
                            INITIAL_SOAP_NOTE_LABELS.causing
                        )}
                        <Controller
                            name="causing"
                            control={control}
                            render={({ field }) => (
                                <Select
                                    id="causing"
                                    inputId="causing"
                                    placeholder=""
                                    isRequired
                                    onChange={(val) =>
                                        field.onChange((val as Option).value)
                                    }
                                    value={
                                        CausingOptionsForSelect.find(
                                            (cause) =>
                                                cause.value === field.value
                                        ) || null
                                    }
                                    options={CausingOptionsForSelect}
                                    hasError={!!errors.causing}
                                    errorText={errors.causing?.message}
                                />
                            )}
                        />
                    </div>
                    <div className="fg">
                        {renderAiReviewFieldHeading(
                            "intention_for_session",
                            INITIAL_SOAP_NOTE_LABELS.intention_for_session
                        )}
                        <Controller
                            name="intention_for_session"
                            control={control}
                            render={({ field }) => (
                                <Select
                                    id="intention_for_session"
                                    inputId="intention_for_session"
                                    placeholder=""
                                    onChange={(val) =>
                                        field.onChange((val as Option).value)
                                    }
                                    value={
                                        IntentionForSessionForSelect.find(
                                            (session) =>
                                                session.value === field.value
                                        ) || null
                                    }
                                    options={IntentionForSessionForSelect}
                                    hasError={!!errors.intention_for_session}
                                    errorText={
                                        errors.intention_for_session?.message
                                    }
                                    isRequired
                                />
                            )}
                        />
                    </div>
                </section>
                <section id="step-two" className="mb-10">
                    <h3 className="note-header">Session Note</h3>
                    {renderAiReviewFieldHeading(
                        "subjective",
                        INITIAL_SOAP_NOTE_LABELS.subjective
                    )}
                    <p className="note-label">
                        This section is for Subjective reporting of your
                        clients, it can include their mood, their reported
                        symptoms, their efforts since your last meeting to
                        implement your homework or recommendations or any
                        questions they have
                    </p>
                    <div className="fg">
                        <TextArea
                            {...register("subjective")}
                            id="subjective"
                            hasError={!!errors.subjective}
                            errorText={errors.subjective?.message}
                            defaultValue={noteDetails.note_content?.subjective}
                        />
                        {renderAiWriterFieldCue("subjective")}
                    </div>
                    {renderAiReviewFieldHeading(
                        "objective",
                        INITIAL_SOAP_NOTE_LABELS.objective
                    )}
                    <p className="note-label">
                        What were the behaviors, nonverbal expressions,gestures,
                        postures, and overall presentation of the client?
                        Consider client&apos;s mood and affect,client&apos;s
                        response to treatment, any use of assessments. How did
                        they present themselves? Was there nervous talking or
                        lack of eye contact?
                    </p>
                    <div className="fg">
                        <TextArea
                            {...register("objective")}
                            id="objective"
                            hasError={!!errors.objective}
                            errorText={errors.objective?.message}
                            defaultValue={noteDetails.note_content?.objective}
                        />
                        {renderAiWriterFieldCue("objective")}
                    </div>
                    {renderAiReviewFieldHeading(
                        "assessment",
                        INITIAL_SOAP_NOTE_LABELS.assessment
                    )}
                    <p className="note-label">
                        This section is using clinical judgment and analysis to
                        provide a combined summary of session&apos;s focus. This
                        should not be a mere repeat of the S & O sections, but
                        rather a synthesis of those sections that displays a
                        greater understanding of the client as revealed during
                        that particular therapy session. Which clinical themes
                        are present? What do the subjective reports of the
                        client and objective observations really show about the
                        current state of the client?
                    </p>
                    <p className="note-label">
                        Assessment reflects clinical themes, engagement, and
                        response to treatment. Diagnostic determinations are
                        made by a licensed clinician.
                    </p>
                    <div className="fg">
                        <TextArea
                            {...register("assessment")}
                            id="assessment"
                            hasError={!!errors.assessment}
                            errorText={errors.assessment?.message}
                            defaultValue={noteDetails.note_content?.assessment}
                        />
                        {renderAiWriterFieldCue("assessment")}
                    </div>
                </section>
                <section id="step-three" className="mb-10">
                    <h3 className="note-header">
                        Therapy Intervention Techniques
                    </h3>
                    <p className="note-label">
                        Please select at least 1 intervention used
                    </p>
                    <p className="note-label note-label-opt">
                        {
                            INITIAL_SOAP_NOTE_LABELS.cognitive_behavioral_therapies
                        }
                    </p>
                    <div className="note-checkboxes-list">
                        <FormCheckbox
                            {...register("cognitive_behavioral_therapies")}
                            label="Cognitive-Behavioral therapy (CBT)"
                            value="Cognitive-Behavioral therapy (CBT)"
                            defaultChecked={handleCheckCognitive(
                                "Cognitive-Behavioral therapy (CBT)"
                            )}
                        />
                        <FormCheckbox
                            {...register("cognitive_behavioral_therapies")}
                            label="Dialectical Behavioral therapy (DBT)"
                            value="Dialectical Behavioral therapy (DBT)"
                            defaultChecked={handleCheckCognitive(
                                "Dialectical Behavioral therapy (DBT)"
                            )}
                        />
                        <FormCheckbox
                            {...register("cognitive_behavioral_therapies")}
                            label="Modeling and skills training"
                            value="Modeling and skills training"
                            defaultChecked={handleCheckCognitive(
                                "Modeling and skills training"
                            )}
                        />
                        <FormCheckbox
                            {...register("cognitive_behavioral_therapies")}
                            label="Trauma-focused CBT"
                            value="Trauma-focused CBT"
                            defaultChecked={handleCheckCognitive(
                                "Trauma-focused CBT"
                            )}
                        />
                        <FormCheckbox
                            {...register("cognitive_behavioral_therapies")}
                            label="EMDR"
                            value="EDMR"
                            defaultChecked={handleCheckCognitive("EDMR")}
                        />
                        <FormCheckbox
                            {...register("cognitive_behavioral_therapies")}
                            label="Rational Emotive Behaviour therapy"
                            value="Rational Emotive Behaviour therapy"
                            defaultChecked={handleCheckCognitive(
                                "Rational Emotive Behaviour therapy"
                            )}
                        />
                        <FormCheckbox
                            {...register("cognitive_behavioral_therapies")}
                            label="Acceptance Commitment Therapy"
                            value="Acceptance Commitment Therapy"
                            defaultChecked={handleCheckCognitive(
                                "Acceptance Commitment Therapy"
                            )}
                        />
                        <FormCheckbox
                            {...register("cognitive_behavioral_therapies")}
                            label="Solution Based Brief Therapy"
                            value="Solution Based Brief Therapy"
                            defaultChecked={handleCheckCognitive(
                                "Solution Based Brief Therapy"
                            )}
                        />
                        <FormCheckbox
                            {...register("cognitive_behavioral_therapies")}
                            label="Mindfulness Based Cognitive Therapy"
                            value="Mindfulness Based Cognitive Therapy"
                            defaultChecked={handleCheckCognitive(
                                "Mindfulness Based Cognitive Therapy"
                            )}
                        />
                    </div>
                    <p className="note-label note-label-opt">
                        {
                            INITIAL_SOAP_NOTE_LABELS.relationship_based_interventions
                        }
                    </p>
                    <div className="note-checkboxes-list">
                        <FormCheckbox
                            {...register("relationship_based_interventions")}
                            label="Attachment-oriented interventions"
                            value="Attachment-oriented interventions"
                            defaultChecked={handleCheckRelationship(
                                "Attachment-oriented interventions"
                            )}
                        />
                        <FormCheckbox
                            {...register("relationship_based_interventions")}
                            label="Parent-child interaction therapy"
                            value="Parent-child interaction therapy"
                            defaultChecked={handleCheckRelationship(
                                "Parent-child interaction therapy"
                            )}
                        />
                        <FormCheckbox
                            {...register("relationship_based_interventions")}
                            label="Parent interventions"
                            value="Parent interventions"
                            defaultChecked={handleCheckRelationship(
                                "Parent interventions"
                            )}
                        />
                    </div>
                    <p className="note-label note-label-opt">
                        {INITIAL_SOAP_NOTE_LABELS.other}
                    </p>
                    <div className="note-checkboxes-list">
                        <FormCheckbox
                            {...register("other")}
                            label="Client centered therapy/ Humanism"
                            value="Client centered therapy/ Humanism"
                            defaultChecked={handleCheckOther(
                                "Client centered therapy/ Humanism"
                            )}
                        />
                        <FormCheckbox
                            {...register("other")}
                            label="Gestalt therapy"
                            value="Gestalt therapy"
                            defaultChecked={handleCheckOther("Gestalt therapy")}
                        />
                        <FormCheckbox
                            {...register("other")}
                            label="Existential therapy"
                            value="Existential therapy"
                            defaultChecked={handleCheckOther(
                                "Existential therapy"
                            )}
                        />
                        <FormCheckbox
                            {...register("other")}
                            label="Feminist therapy"
                            value="Feminist therapy"
                            defaultChecked={handleCheckOther(
                                "Feminist therapy"
                            )}
                        />
                        <FormCheckbox
                            {...register("other")}
                            label="Psychodynamic therapy"
                            value="Psychodynamic therapy"
                            defaultChecked={handleCheckOther(
                                "Psychodynamic therapy"
                            )}
                        />
                        <FormCheckbox
                            {...register("other")}
                            label="Grief therapy"
                            value="Grief therapy"
                            defaultChecked={handleCheckOther("Grief therapy")}
                        />
                        <FormCheckbox
                            {...register("other")}
                            label="Internal family systems (IFS)"
                            value="Internal family systems (IFS)"
                            defaultChecked={handleCheckOther(
                                "Internal family systems (IFS)"
                            )}
                        />
                        <FormCheckbox
                            {...register("other")}
                            label="Narrative therapy"
                            value="Narrative therapy"
                            defaultChecked={handleCheckOther(
                                "Narrative therapy"
                            )}
                        />
                        <FormCheckbox
                            {...register("other")}
                            label="Positive psychology"
                            value="Positive psychology"
                            defaultChecked={handleCheckOther(
                                "Positive psychology"
                            )}
                        />
                        <FormCheckbox
                            {...register("other")}
                            label="Psychoeducation"
                            value="Psychoeducation"
                            defaultChecked={handleCheckOther("Psychoeducation")}
                        />
                        <FormCheckbox
                            {...register("other")}
                            label="Sex therapy"
                            value="Sex therapy"
                            defaultChecked={handleCheckOther("Sex therapy")}
                        />
                        <FormCheckbox
                            {...register("other")}
                            label="Strength based theory"
                            value="Strength based theory"
                            defaultChecked={handleCheckOther(
                                "Strength based theory"
                            )}
                        />
                        <FormCheckbox
                            {...register("other")}
                            label="Career Counseling"
                            value="Career Counseling"
                            defaultChecked={handleCheckOther(
                                "Career Counseling"
                            )}
                        />
                        <FormCheckbox
                            {...register("other")}
                            label="Multisystemic family theory"
                            value="Multisystemic family theory"
                            defaultChecked={handleCheckOther(
                                "Multisystemic family theory"
                            )}
                        />
                    </div>
                    {rootError ? (
                        <div className="note-error">
                            <p>{rootError}</p>
                        </div>
                    ) : null}

                    {renderAiReviewFieldHeading(
                        "plan",
                        INITIAL_SOAP_NOTE_LABELS.plan
                    )}
                    <p className="note-label">
                        What specific steps has the client committed to work on
                        as homework or during the next session? What specific
                        interventions or treatment plan changes will the
                        clinician be focused on in the upcoming sessions?
                    </p>
                    <div className="fg">
                        <TextArea
                            {...register("plan")}
                            id="plan"
                            hasError={!!errors.plan}
                            errorText={errors.plan?.message}
                            defaultValue={noteDetails.note_content?.plan}
                        />
                        {renderAiWriterFieldCue("plan")}
                    </div>
                    <h3 className="note-label-req">
                        {INITIAL_SOAP_NOTE_LABELS.frequency_of_sessions}
                    </h3>
                    <div className="note-radios">
                        <RadioInput
                            {...register("frequency_of_sessions")}
                            label="Weekly"
                            value="Weekly"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.frequency_of_sessions === "Weekly"
                            }
                        />
                        <RadioInput
                            {...register("frequency_of_sessions")}
                            label="Bi-Weekly"
                            value="Bi-Weekly"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.frequency_of_sessions === "Bi-Weekly"
                            }
                        />
                        <RadioInput
                            {...register("frequency_of_sessions")}
                            label="Monthly"
                            value="Monthly"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.frequency_of_sessions === "Monthly"
                            }
                        />
                        <RadioInput
                            {...register("frequency_of_sessions")}
                            label="Other"
                            value="Other"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.frequency_of_sessions === "Other"
                            }
                        />
                    </div>
                    {errors.frequency_of_sessions ? (
                        <div className="note-error">
                            <p>Frequency of sessions is required</p>
                        </div>
                    ) : null}
                    {watch("frequency_of_sessions") === "Other" && (
                        <div className="fg">
                            <Input
                                {...register("other_frequency_of_sessions")}
                                label={
                                    INITIAL_SOAP_NOTE_LABELS.other_frequency_of_sessions
                                }
                                hasError={!!errors.other_frequency_of_sessions}
                                errorText={
                                    errors.other_frequency_of_sessions?.message
                                }
                                defaultValue={
                                    noteDetails.note_content
                                        ?.other_frequency_of_sessions
                                }
                            />
                        </div>
                    )}
                    <div className="fg">
                        <Controller
                            name="date_of_next_session"
                            control={control}
                            render={({ field }) => (
                                <DatePicker
                                    label={
                                        INITIAL_SOAP_NOTE_LABELS.date_of_next_session
                                    }
                                    onChange={(date) => {
                                        field.onChange(date);
                                        setLocalDateOfNextSession(date);
                                    }}
                                    selected={localDateOfNextSession}
                                    value={
                                        field.value
                                            ? formatDate(
                                                  field.value,
                                                  true,
                                                  "MM/dd/yyyy"
                                              )
                                            : undefined
                                    }
                                    hasError={!!errors.date_of_next_session}
                                    errorText={
                                        errors.date_of_next_session?.type ===
                                        "typeError"
                                            ? "Invalid date value"
                                            : errors.date_of_next_session
                                                  ?.message
                                    }
                                    shouldCloseOnSelect
                                    isRequired
                                    minDate={
                                        noteDetails.date_of_service
                                            ? addDays(
                                                  parseISO(
                                                      noteDetails.date_of_service
                                                  ),
                                                  1
                                              )
                                            : undefined
                                    }
                                />
                            )}
                        />
                    </div>
                    {renderAiReviewFieldHeading(
                        "prognosis",
                        INITIAL_SOAP_NOTE_LABELS.prognosis
                    )}
                    <p className="note-label">
                        Please write at least 1-2 sentences about the likely
                        outcome/development of the symptoms/challenges
                    </p>
                    <div className="fg">
                        <TextArea
                            {...register("prognosis")}
                            id="prognosis"
                            defaultValue={noteDetails.note_content?.prognosis}
                            hasError={!!errors.prognosis}
                            errorText={errors.prognosis?.message}
                            isRequired
                        />
                        {renderAiWriterFieldCue("prognosis")}
                    </div>
                    {renderAiReviewFieldHeading(
                        "client_response",
                        INITIAL_SOAP_NOTE_LABELS.client_response
                    )}
                    <div className="fg">
                        <TextArea
                            {...register("client_response")}
                            id="client_response"
                            hasError={!!errors.client_response}
                            errorText={errors.client_response?.message}
                            defaultValue={
                                noteDetails.note_content?.client_response
                            }
                        />
                        {renderAiWriterFieldCue("client_response")}
                    </div>
                </section>
                <section id="step-four" className="mb-10">
                    <h3 className="note-header">Treatment Plan/ Progress</h3>
                    <div className="fg fg-space-between two flex">
                        <div className="w-full">
                            {renderAiReviewFieldHeading(
                                "goal_one",
                                INITIAL_SOAP_NOTE_LABELS.goal_one
                            )}
                            <TextArea
                                {...register("goal_one")}
                                id="goal_one"
                                hasError={!!errors.goal_one}
                                errorText={errors.goal_one?.message}
                                defaultValue={
                                    noteDetails.note_content?.goal_one
                                }
                                isRequired
                            />
                        </div>
                        <div className="w-full">
                            {renderAiReviewFieldHeading(
                                "progress_one",
                                INITIAL_SOAP_NOTE_LABELS.progress_one
                            )}
                            <TextArea
                                {...register("progress_one")}
                                id="progress_one"
                                hasError={!!errors.progress_one}
                                errorText={errors.progress_one?.message}
                                defaultValue={
                                    noteDetails.note_content?.progress_one
                                }
                                isRequired
                            />
                            {renderAiWriterFieldCue("progress_one")}
                        </div>
                    </div>
                    <div className="fg fg-space-between two flex">
                        <div className="w-full">
                            {renderAiReviewFieldHeading(
                                "goal_two",
                                INITIAL_SOAP_NOTE_LABELS.goal_two
                            )}
                            <TextArea
                                {...register("goal_two")}
                                id="goal_two"
                                hasError={!!errors.goal_two}
                                errorText={errors.goal_two?.message}
                                defaultValue={
                                    noteDetails.note_content?.goal_two
                                }
                                isRequired
                            />
                        </div>
                        <div className="w-full">
                            {renderAiReviewFieldHeading(
                                "progress_two",
                                INITIAL_SOAP_NOTE_LABELS.progress_two
                            )}
                            <TextArea
                                {...register("progress_two")}
                                id="progress_two"
                                hasError={!!errors.progress_two}
                                errorText={errors.progress_two?.message}
                                defaultValue={
                                    noteDetails.note_content?.progress_two
                                }
                                isRequired
                            />
                            {renderAiWriterFieldCue("progress_two")}
                        </div>
                    </div>
                    <div className="fg fg-space-between two flex">
                        <div className="w-full">
                            {renderAiReviewFieldHeading(
                                "goal_three",
                                INITIAL_SOAP_NOTE_LABELS.goal_three,
                                "note-label-opt"
                            )}
                            <TextArea
                                {...register("goal_three")}
                                id="goal_three"
                                hasError={!!errors.goal_three}
                                errorText={errors.goal_three?.message}
                                defaultValue={
                                    noteDetails.note_content?.goal_three
                                }
                                isOptional
                            />
                        </div>
                        <div className="w-full">
                            {renderAiReviewFieldHeading(
                                "progress_three",
                                INITIAL_SOAP_NOTE_LABELS.progress_three,
                                "note-label-opt"
                            )}
                            <TextArea
                                {...register("progress_three")}
                                id="progress_three"
                                hasError={!!errors.progress_three}
                                errorText={errors.progress_three?.message}
                                defaultValue={
                                    noteDetails.note_content?.progress_three
                                }
                                isOptional
                            />
                            {renderAiWriterFieldCue("progress_three")}
                        </div>
                    </div>
                    <h3 className="note-label note-label-req mt-4">
                        {INITIAL_SOAP_NOTE_LABELS.risk_and_safety_language}
                    </h3>
                    {renderAiReviewFieldAnnotation("risk_and_safety_language")}
                    <div className="fg fg-space-between two flex">
                        <RadioInput
                            {...register("risk_and_safety_language")}
                            label="Risk reviewed; no change from intake (intake conducted by licensed clinician)"
                            value="Risk reviewed; no change from intake (intake conducted by licensed clinician)"
                        />
                        <RadioInput
                            {...register("risk_and_safety_language")}
                            label="New risk concerns reviewed with supervisor"
                            value="New risk concerns reviewed with supervisor"
                        />
                    </div>
                    {errors.risk_and_safety_language ? (
                        <div className="note-error">
                            <p>Risk and safety language is required</p>
                        </div>
                    ) : null}
                    {watch("risk_and_safety_language") ===
                        "New risk concerns reviewed with supervisor" && (
                        <div>
                            <h3 className="note-label note-label-req">
                                {
                                    INITIAL_SOAP_NOTE_LABELS.new_risk_concerns_details
                                }
                            </h3>
                            {renderAiReviewFieldAnnotation(
                                "new_risk_concerns_details"
                            )}
                            <div className="fg">
                                <TextArea
                                    {...register("new_risk_concerns_details")}
                                    id="new_risk_concerns_details"
                                    hasError={
                                        !!errors.new_risk_concerns_details
                                    }
                                    errorText={
                                        errors.new_risk_concerns_details
                                            ?.message
                                    }
                                    defaultValue={
                                        noteDetails.note_content
                                            ?.new_risk_concerns_details
                                    }
                                />
                            </div>
                        </div>
                    )}
                    <h3 className="note-label note-label-opt mt-4">
                        {
                            INITIAL_SOAP_NOTE_LABELS.additional_client_reported_information_post_intake
                        }
                    </h3>
                    {renderAiReviewFieldAnnotation(
                        "additional_client_reported_information_post_intake"
                    )}
                    <p className="note-label">
                        Use this section to document new client-reported
                        information that was not disclosed or fully discussed
                        during the initial intake. Do not repeat intake content,
                        conduct a new assessment, or document diagnostic
                        conclusions
                    </p>
                    <div className="fg">
                        <TextArea
                            {...register(
                                "additional_client_reported_information_post_intake"
                            )}
                            id="additional_client_reported_information_post_intake"
                            hasError={
                                !!errors.additional_client_reported_information_post_intake
                            }
                            errorText={
                                errors
                                    .additional_client_reported_information_post_intake
                                    ?.message
                            }
                            defaultValue={
                                noteDetails.note_content
                                    ?.additional_client_reported_information_post_intake
                            }
                        />
                    </div>
                </section>
                <div className="fg-info fg-line">
                    <p>Sign note here</p>
                    <div className="fg">
                        <Input
                            {...register("signature")}
                            hasError={!!errors.signature}
                            errorText={errors.signature?.message}
                            label="Provider's Initials"
                            autoComplete="off"
                            isRequired
                        />
                    </div>
                </div>
                <div>
                    <EditNoteFooter
                        onSaveButtonClick={() => {
                            isSigning.current = false;
                            handleSubmit(handleEditDraftNote)();
                        }}
                        noteDOS={noteDetails.date_of_service}
                        onSubmitButtonClick={() => {
                            isSigning.current = true;
                        }}
                        submitBtnIsDisabled={isAiSubmitReviewPending}
                        submitButtonLabel={submitButtonLabel}
                    />
                </div>
            </form>
        </div>
    );
}

export default function EditInitialSoapNote({
    noteDetails,
}: EditInitialSoapNoteProps) {
    return (
        <AiSessionNoteReviewBoundary noteId={noteDetails.note_id}>
            <EditInitialSoapNoteForm noteDetails={noteDetails} />
        </AiSessionNoteReviewBoundary>
    );
}
